import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Container,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
  Box,
} from '@mui/material';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useCatch } from '../reactHelper';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import PremiumInput from '../common/components/premium/PremiumInput';

const CommandGroupPage = () => {
  const navigate = useNavigate();
  const { classes } = useSettingsStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const { id } = useParams();

  const textEnabled = useSelector((state) => state.session.server.textEnabled);

  const [item, setItem] = useState({ type: 'custom', attributes: {} });

  const handleSend = useCatch(async () => {
    const query = new URLSearchParams({ groupId: id });
    await fetchOrThrow(`/api/commands/send?${query.toString()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    });
    navigate(-1);
  });

  const checkboxSx = {
    color: tokens.border.default,
    '&.Mui-checked': { color: tokens.accent.primary },
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'deviceCommand']}>
      <Container maxWidth={false} className={classes.container}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: tokens.radius.lg,
            backgroundColor: tokens.surface.card,
            border: `1px solid ${tokens.border.subtle}`,
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, color: tokens.text.primary }}>
            {t('sharedRequired')}
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <Box
              display="flex"
              width="100%"
              gap={2}
              sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
            >
              <Box flex={1} minWidth={0}>
                <FormControl fullWidth size="small" className="premium-input">
                  <InputLabel>{t('sharedType')}</InputLabel>
                  <Select label={t('sharedType')} value="custom" disabled>
                    <MenuItem value="custom">{t('commandCustom')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box flex={1} minWidth={0}>
                <PremiumInput
                  value={item.attributes.data || ''}
                  onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, data: e.target.value } })}
                  label={t('commandData')}
                />
              </Box>
            </Box>
            {textEnabled && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={item.textChannel}
                    onChange={(event) => setItem({ ...item, textChannel: event.target.checked })}
                    sx={checkboxSx}
                  />
                }
                label={t('commandSendSms')}
              />
            )}
          </Box>
        </Paper>
        <div className={classes.buttons}>
          <Button
            type="button"
            color="primary"
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: tokens.radius.md,
              borderColor: tokens.border.default,
              '&:hover': { borderColor: tokens.accent.primary },
            }}
          >
            {t('sharedCancel')}
          </Button>
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={handleSend}
            disabled={!item.attributes.data}
            sx={{
              borderRadius: tokens.radius.md,
              backgroundColor: tokens.accent.primary,
              '&:hover': { backgroundColor: tokens.accent.hover },
            }}
          >
            {t('commandSend')}
          </Button>
        </div>
      </Container>
    </PageLayout>
  );
};

export default CommandGroupPage;
